import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import GoogleMapsService from '../../services/googleMapsService';

interface MapDisplayProps {
  latitude?: number;
  longitude?: number;
  address?: string;
  height?: string | number;
  zoom?: number;
  onLocationChange?: (lat: number, lng: number) => void;
  draggable?: boolean;
}

const GoogleMapDisplay: React.FC<MapDisplayProps> = ({
  latitude,
  longitude,
  address,
  height = 250,
  zoom = 15,
  onLocationChange,
  draggable = true
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<google.maps.Marker | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const loadGoogleMaps = async () => {
      try {
        const googleMapsService = GoogleMapsService.getInstance();
        await googleMapsService.loadGoogleMaps();
        
        if (mapRef.current) {
          const initialCenter = latitude && longitude 
            ? { lat: latitude, lng: longitude }
            : { lat: 40.7128, lng: -74.0060 }; // Default to NYC

          const mapInstance = new google.maps.Map(mapRef.current, {
            center: initialCenter,
            zoom: zoom,
            styles: [
              {
                "featureType": "all",
                "elementType": "geometry.fill",
                "stylers": [{ "color": "#1a202c" }]
              },
              {
                "featureType": "all",
                "elementType": "labels.text.fill",
                "stylers": [{ "color": "#b0b8c1" }]
              },
              {
                "featureType": "all",
                "elementType": "labels.text.stroke",
                "stylers": [{ "color": "#1a202c" }]
              },
              {
                "featureType": "road",
                "elementType": "geometry",
                "stylers": [{ "color": "#2a3441" }]
              },
              {
                "featureType": "water",
                "elementType": "geometry",
                "stylers": [{ "color": "#377dff" }]
              }
            ]
          });

          setMap(mapInstance);

          // Add click listener to map to set location (always available)
          if (onLocationChange) {
            mapInstance.addListener('click', (event: any) => {
              const clickedLat = event.latLng.lat();
              const clickedLng = event.latLng.lng();
              
              // Create or update marker at clicked location
              if (marker) {
                marker.setPosition({ lat: clickedLat, lng: clickedLng });
              } else {
                const clickMarker = new google.maps.Marker({
                  position: { lat: clickedLat, lng: clickedLng },
                  map: mapInstance,
                  draggable: draggable,
                  icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 8,
                    fillColor: '#377dff',
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: 2,
                  }
                });
                
                if (draggable) {
                  clickMarker.addListener('dragend', (dragEvent: any) => {
                    const newLat = dragEvent.latLng.lat();
                    const newLng = dragEvent.latLng.lng();
                    onLocationChange(newLat, newLng);
                  });
                }
                
                setMarker(clickMarker);
              }
              
              onLocationChange(clickedLat, clickedLng);
            });
          }

          if (latitude && longitude) {
            const markerInstance = new google.maps.Marker({
              position: { lat: latitude, lng: longitude },
              map: mapInstance,
              title: address || 'Selected Location',
              draggable: draggable,
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: '#377dff',
                fillOpacity: 1,
                strokeColor: '#fff',
                strokeWeight: 2,
              }
            });
            
            // Add drag listener
            if (draggable && onLocationChange) {
              markerInstance.addListener('dragend', (event: any) => {
                const newLat = event.latLng.lat();
                const newLng = event.latLng.lng();
                onLocationChange(newLat, newLng);
              });
            }
            
            setMarker(markerInstance);
          }
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading Google Maps:', error);
        setLoadError('Failed to load Google Maps');
        setIsLoading(false);
      }
    };

    loadGoogleMaps();
  }, []);

  useEffect(() => {
    if (map && latitude !== undefined && longitude !== undefined && latitude !== null && longitude !== null) {
      const newCenter = { lat: latitude, lng: longitude };
      
      // Update map center and zoom immediately
      map.setCenter(newCenter);
      map.setZoom(zoom);

      // Remove existing marker if it exists
      if (marker) {
        marker.setMap(null);
        // Clear any existing listeners to prevent memory leaks
        google.maps.event.clearInstanceListeners(marker);
      }

      // Create new marker at the new location
      const newMarker = new google.maps.Marker({
        position: newCenter,
        map: map,
        title: address || 'Selected Location',
        draggable: draggable,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#377dff',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
        }
      });
      
      // Add drag listener if draggable and callback provided
      if (draggable && onLocationChange) {
        newMarker.addListener('dragend', (event: any) => {
          const newLat = event.latLng.lat();
          const newLng = event.latLng.lng();
          onLocationChange(newLat, newLng);
        });
      }
      
      // Update marker state
      setMarker(newMarker);
    }
  }, [map, latitude, longitude, address, zoom, draggable, onLocationChange]);

  if (loadError) {
    return (
      <Box
        sx={{
          height: height,
          backgroundColor: '#1a202c',
          border: '1px solid #4a5568',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          color: '#ef4444'
        }}
      >
        <Typography variant="body2" sx={{ color: '#ef4444', textAlign: 'center' }}>
          {loadError}
        </Typography>
        <Typography variant="caption" sx={{ color: '#6b7280', textAlign: 'center', mt: 1 }}>
          Please check your Google Maps API configuration
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: height,
        backgroundColor: '#1a202c',
        border: '1px solid #4a5568',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#1a202c',
            zIndex: 1
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#377dff', mb: 2 }} />
            <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
              Loading map...
            </Typography>
          </Box>
        </Box>
      )}
      
      <div
        ref={mapRef}
        style={{
          width: '100%',
          height: '100%',
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.3s ease'
        }}
      />
      
      {!isLoading && !latitude && !longitude && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: '#6b7280',
            pointerEvents: 'none'
          }}
        >
          <Typography variant="body2">
            Enter an address to see location on map
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default GoogleMapDisplay;
